import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "../../../../Axios";
import { Urls } from "../../../../Urls";
import Loader from "../../../Loader";
import classes from "../../indenttrack.module.css";
import { toast } from "react-toastify";
import { sqlInjectionPatterns } from "../../../../Utility";
import { closeModal } from "../../InvoiceV2/utils";
import { MyCompanyProfileContext } from "../../../../MyCompanyProfileContext";
import { useIndentTracking } from "../../hooks/useIndentTracking";
import ShowLrHistory from "./ShowLrHistory";
import lrClasses from "./LrHistory.module.css";
import PopupModal from "../../../Shared/PopupModal/PopupModal";

const ConsignmentDetails = ({
  indent_id,
  indent_sub_id,
  origin,
  destination,
  lr_ids,
  userDefinedFields: propUserDefinedFields = [],
  readOnly = false,
  isPlanning = false,
  details = {},
  getVehicleTracking = () => { },
  hideSubmitButton = false,
  loading = false,
  isRestricted = false,
  isDashboardView = false,
}) => {
  const [consignmentData, setConsignmentData] = useState([]);
  const [consignorData, setConsignorData] = useState({});
  const [consigneeData, setConsigneeData] = useState({});
  const [invoiceCount, setInvoiceCount] = useState(Number(0));
  const [show, setShow] = useState(false);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { trackIndentEvent } = useIndentTracking();

  const userDefinedFields = useMemo(() => {
    if (propUserDefinedFields && propUserDefinedFields.length > 0)
      return propUserDefinedFields;

    const fields = new Set();
    consignmentData?.forEach((c) => {
      c.orders_list?.forEach((o) => {
        const customFields = o.custom_consignment_fields;
        if (Array.isArray(customFields)) {
          customFields.forEach((f) => f.name && fields.add(f.name));
        }
      });
    });
    return Array.from(fields).map((name) => ({ field: name }));
  }, [propUserDefinedFields, consignmentData]);

  useEffect(() => {
    if (!details) return;

    const {
      consignment_details = [],
      consignor_details = {},
      consignee_details = {},
    } = details;

    if (consignment_details.length) {
      const flattenedDetails = consignment_details.map((consignment) => {
        const orders = consignment.orders_list || [];

        const flattenedOrders = orders.map((order) => {
          let flattenedOrder = { ...order };

          if (readOnly) flattenedOrder.isNew = false;
          else if (isPlanning) flattenedOrder.isNew = true;

          const customFields = order.custom_consignment_fields;
          if (customFields) {
            if (Array.isArray(customFields)) {
              customFields.forEach(({ name, value }) => {
                if (name) flattenedOrder[name] = value;
              });
            } else if (typeof customFields === "object") {
              Object.assign(flattenedOrder, customFields);
            }
          }

          return flattenedOrder;
        });

        return {
          ...consignment,
          orders_list: flattenedOrders,
        };
      });

      setConsignmentData(flattenedDetails);

      const invoiceCount = consignment_details.reduce(
        (acc, c) => acc + (c.no_of_orders || c.orders_list?.length || 0),
        0,
      );
      setInvoiceCount(invoiceCount);
    }

    setConsignorData({
      customer_name:
        consignor_details.customer_name || origin?.customer_name || "",
      gst_no: consignor_details.gst_no || origin?.gst_no || "",
      addressLine1:
        consignor_details.addressLine1 || origin?.addressLine1 || "",
    });

    setConsigneeData({
      customer_name:
        consignee_details.customer_name || destination?.customer_name || "",
      gst_no: consignee_details.gst_no || destination?.gst_no || "",
      addressLine1:
        consignee_details.addressLine1 || destination?.addressLine1 || "",
    });
  }, [details, origin, destination, readOnly, isPlanning]);

  const fetchMaterialDetails = async (code, index, idx) => {
    const trimmedCode = code?.trim();
    if (!trimmedCode) {
      toast.error("Material Code cannot be empty");
      return;
    }
    try {
      const resp = await axios.post(
        `${Urls.freight_master}?action=clean_material_master`,
        { material_code: trimmedCode },
      );

      if (resp?.data?.status === false) {
        toast.error(resp?.data?.error + " for code : " + trimmedCode);
        setConsignmentData(
          (prev) =>
            prev?.length > 0 &&
            prev.map((c, ci) => {
              if (ci !== index) return c;
              return {
                ...c,
                orders_list:
                  c.orders_list?.length > 0 &&
                  c.orders_list.map((o, oi) => {
                    if (oi !== idx) return o;
                    return {
                      ...o,
                      material_name: "",
                      material_weight: "",
                      quantity: "",
                      invoice_value: "",
                      material_volume: "",
                    };
                  }),
              };
            }),
        );
        return;
      } else if (resp?.data?.status && resp?.data?.material_code) {
        setConsignmentData(
          (prev) =>
            prev?.length > 0 &&
            prev.map((c, ci) => {
              if (ci !== index) return c;
              return {
                ...c,
                orders_list:
                  c.orders_list?.length > 0 &&
                  c.orders_list.map((o, oi) => {
                    if (oi !== idx) return o;
                    return {
                      ...o,
                      material_name:
                        resp?.data?.material_code?.material_description || "",
                      material_weight: resp?.data?.material_code?.weight || "",
                      material_volume: resp?.data?.material_code?.volume || "",
                    };
                  }),
              };
            }),
        );
        toast.success("Material detailed fetched for the code.");
      } else {
        toast.info("Please try again after some time.");
        return;
      }
    } catch (error) {
      console.log("Error while fetching material details", error);
    }
  };

  const handleAddRow = () => {
    setConsignmentData((prevData) => [
      ...prevData,
      {
        invoice_no: "",
        no_of_orders: Number(1),
        orders_list: [
          {
            quantity: 0,
            material_code: "",
            material_name: "",
            material_weight: 0,
            material_volume: 0,
            item_id: "",
            invoice_value: 0,
            isNew: true,
          },
        ],
      },
    ]);
    setInvoiceCount(invoiceCount + 1);
  };

  const handleRemoveRow = () => {
    setConsignmentData((prev) => {
      if (prev.length === 0) {
        setInvoiceCount(0);
        return prev;
      }
      const last = prev[prev.length - 1];

      if (!last || !last.orders_list?.[last.orders_list.length - 1]?.isNew) {
        return prev;
      }

      const newList = prev.slice(0, -1);
      setInvoiceCount((c) => Math.max(c - 1, 0));
      return newList;
    });
  };

  const isValidGSTNumber = (gstNumber) => {
    const gstRegex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
    return gstRegex.test(gstNumber?.toUpperCase());
  };

  const updateConsignmentField = (cIndex, field, value) => {
    setConsignmentData((prev) =>
      prev.map((c, ci) => (ci !== cIndex ? c : { ...c, [field]: value })),
    );
  };

  const updateOrderField = (cIndex, oIndex, field, value) => {
    const customField = userDefinedFields?.find((f) => f.field === field);
    const isTextField =
      field === "material_code" ||
      field === "material_name" ||
      customField?.data_type === "text";

    if (value === "" && !isTextField) value = 0;
    if (field === "material_code") value = value?.trim();
    setConsignmentData((prev) =>
      prev.map((c, ci) =>
        ci !== cIndex
          ? c
          : {
            ...c,
            orders_list: c.orders_list.map((o, oi) =>
              oi !== oIndex ? o : { ...o, [field]: value },
            ),
          },
      ),
    );
  };

  const handleSubmit = (consignee, consignor) => {
    const isMaterialNameValid = consignmentData?.some((consignment) =>
      consignment.orders_list?.some((order) =>
        sqlInjectionPatterns.test(order?.material_name),
      ),
    );

    const isDocumentNoValid = consignmentData?.some((consignment) =>
      sqlInjectionPatterns.test(consignment.invoice_no),
    );

    let isCustomFieldValid = true;
    let missingCustomFieldName = "";

    if (consignmentData?.length > 0 && userDefinedFields?.length > 0) {
      for (const consignment of consignmentData) {
        if (!isCustomFieldValid) break;
        for (const order of consignment.orders_list) {
          if (!isCustomFieldValid) break;
          if (order.isNew) {
            for (const field of userDefinedFields) {
              const value = order[field.field];
              if (value === "" || value === null || value === undefined) {
                isCustomFieldValid = false;
                missingCustomFieldName = field.field;
                break;
              }
            }
          }
        }
      }
    }

    var flag = true;
    var flagForInvoiceNo = true;

    const updatedConsignmentData = [];
    consignmentData.map((entry) => {
      const existingEntry = updatedConsignmentData.find(
        (e) => e.invoice_no === entry.invoice_no,
      );
      if (existingEntry) {
        existingEntry.orders_list = existingEntry.orders_list.concat(
          entry.orders_list,
        );
        existingEntry.no_of_orders = existingEntry.orders_list.length;
      } else {
        updatedConsignmentData.push({
          ...entry,
          no_of_orders: entry.orders_list.length,
        });
      }
    });

    if (updatedConsignmentData && updatedConsignmentData.length > 0) {
      if (flag === false) {
        toast.error("Please fill all fields");
        return false;
      }
      if (flagForInvoiceNo === false) {
        toast.error("Please enter valid Document number");
        return false;
      }
      if (sqlInjectionPatterns.test(consignee.addressLine1)) {
        toast.error("Please enter valid Consignee Address");
        return false;
      }
      if (sqlInjectionPatterns.test(consignee.customer_name)) {
        toast.error("Please enter valid Consignee Name");
        return false;
      }
      if (sqlInjectionPatterns.test(consignor.addressLine1)) {
        toast.error("Please enter valid Consignor Address");
        return false;
      }
      if (sqlInjectionPatterns.test(consignor.customer_name)) {
        toast.error("Please enter valid Consignor Name");
        return false;
      }
      if (isDocumentNoValid) {
        toast.error("Please add valid Document No.");
        return false;
      }
      if (isMaterialNameValid) {
        toast.error("Please add valid Material Name");
        return false;
      }
    }

    if (
      (consignor.gst_no &&
        consignor.gst_no !== "" &&
        !isValidGSTNumber(consignor.gst_no)) ||
      (consignee.gst_no &&
        consignee.gst_no !== "" &&
        !isValidGSTNumber(consignee.gst_no))
    ) {
      toast.error("Please enter a valid GST number");
    }
    if (
      consignor.gst_no &&
      consignee.gst_no &&
      consignor.gst_no === consignee.gst_no
    ) {
      toast.error("Consignor and Consignee GST numbers cannot be the same");
    } else {
      setShow(true);

      const payload = {
        lr_details: [
          {
            lr_id: lr_ids && lr_ids?.length > 0 ? lr_ids?.[0] : "",
            consignee_details: {
              customer_name: consignee.customer_name
                ? consignee.customer_name
                : "",
              gst_no: consignee.gst_no ? consignee.gst_no.toUpperCase() : "",
              addressLine1: consignee.addressLine1
                ? consignee.addressLine1
                : "",
              location: destination,
            },
            consignor_details: {
              customer_name: consignor.customer_name
                ? consignor.customer_name
                : "",
              gst_no: consignor.gst_no ? consignor.gst_no.toUpperCase() : "",
              addressLine1: consignor.addressLine1
                ? consignor.addressLine1
                : "",
              location: origin,
            },
            consignment_details: updatedConsignmentData.map((data) => {
              const newData = { ...data };

              if (newData.orders_list && newData.orders_list.length > 0) {
                newData.orders_list = newData.orders_list.map((order) => {
                  const newOrder = { ...order };
                  const customFields = [];

                  if (userDefinedFields && userDefinedFields.length > 0) {
                    userDefinedFields.forEach((field) => {
                      if (newOrder.hasOwnProperty(field.field)) {
                        customFields.push({
                          name: field.field,
                          value: newOrder[field.field],
                        });
                        delete newOrder[field.field];
                      }
                    });
                  }

                  newOrder.custom_consignment_fields = customFields;
                  return newOrder;
                });
              }
              return newData;
            }),
          },
        ],
      };

      axios
        .post(
          `${Urls.invoice}?field=update_consignment_details&indent_id=${indent_id}&indent_id_sub=${indent_sub_id}`,
          payload,
        )
        .then((res) => {
          setShow(false);
          getVehicleTracking(0, 10, 0);
          trackIndentEvent(
            "indent tracking",
            "user has successfully updated consignment details",
            `indent id :${indent_id}, indent sub id :${indent_sub_id}`,
          );
          closeModal();
          toast.success("Consignment details updated successfully");
        })
        .catch((error) => {
          console.log(error);
          setShow(false);
        });
    }
  };

  return (
    <div>
      <Loader show={loading} />
      <div className="text-center">
        <div className={`row text-left align-items-center mb-1 ${classes["details-header-row"]}`}>
          <div className={`col-8 ${classes["text-color-light"]}`} style={{ fontSize: "14px", fontWeight: "600" }}>
            Consignor Details
          </div>
          <div className="col-4 text-right">
            <PopupModal
              buttonLabel={
                <>
                  <i className="fas fa-history"></i>
                  Edit History
                </>
              }
              buttonClass={lrClasses["lr-edit-history-btn"]}
              title="LR Edit History"
              size="lg"
              content={<ShowLrHistory changeLog={details?.change_log || []} />}
              backdrop={true}
            />
          </div>
        </div>
        <div className={`row text-left ${classes["consignor-row"]}`}>
          <div className="col-4">
            <span className={classes["detail-span"]}>Customer Name</span>
            <label>
              <input
                type="text"
                placeholder="Enter name"
                className={classes["detail-input"]}
                onChange={(e) =>
                  setConsignorData((prev) => ({
                    ...prev,
                    customer_name: e.target.value,
                  }))
                }
                defaultValue={
                  consignorData &&
                    consignorData.customer_name &&
                    consignorData.customer_name !== null
                    ? consignorData.customer_name
                    : ""
                }
                readOnly={readOnly || isRestricted}
              />
            </label>
          </div>
          <div className="col-4">
            <span className={classes["detail-span"]}>Address</span>
            <label>
              <input
                type="text"
                placeholder="Enter address"
                className={classes["detail-input"]}
                defaultValue={
                  consignorData &&
                    consignorData.addressLine1 &&
                    consignorData.addressLine1 !== null
                    ? consignorData.addressLine1
                    : ""
                }
                onChange={(e) =>
                  setConsignorData((prev) => ({
                    ...prev,
                    addressLine1: e.target.value,
                  }))
                }
                readOnly={readOnly || isRestricted}
              />
            </label>
          </div>
          <div className="col-4">
            <span className={classes["detail-span"]}>GST No.</span>
            <label>
              <input
                type="text"
                maxLength={15}
                placeholder="Enter GST no."
                className={classes["detail-input"]}
                defaultValue={
                  consignorData &&
                    consignorData.gst_no &&
                    consignorData.gst_no !== null
                    ? consignorData.gst_no
                    : ""
                }
                value={consignorData.gst_no ? consignorData.gst_no : ""}
                onChange={(e) =>
                  setConsignorData((prev) => ({
                    ...prev,
                    gst_no: e.target.value.toUpperCase(),
                  }))
                }
                readOnly={readOnly || isRestricted}
              />
            </label>
          </div>
        </div>
        <div className={`row text-left ${classes["consignee-row"]}`}>
          <div className={`col-12 ${classes["text-color-light"]} ${classes["consignee-heading-mobile"]}`}>
            Consignee Details
          </div>
          <div className="col-4">
            <span className={classes["detail-span"]}>Customer Name</span>
            <label>
              <input
                type="text"
                placeholder="Enter name"
                className={classes["detail-input"]}
                defaultValue={
                  consigneeData &&
                    consigneeData.customer_name &&
                    consigneeData.customer_name !== null
                    ? consigneeData.customer_name
                    : ""
                }
                onChange={(e) =>
                  setConsigneeData((prev) => ({
                    ...prev,
                    customer_name: e.target.value,
                  }))
                }
                readOnly={readOnly || isRestricted}
              />
            </label>
          </div>
          <div className="col-4">
            <span className={classes["detail-span"]}>Address</span>
            <label>
              <input
                type="text"
                placeholder="Enter address"
                className={classes["detail-input"]}
                defaultValue={
                  consigneeData &&
                    consigneeData.addressLine1 &&
                    consigneeData.addressLine1 !== null
                    ? consigneeData.addressLine1
                    : ""
                }
                onChange={(e) =>
                  setConsigneeData((prev) => ({
                    ...prev,
                    addressLine1: e.target.value,
                  }))
                }
                readOnly={readOnly || isRestricted}
              />
            </label>
          </div>
          <div className="col-4">
            <span className={classes["detail-span"]}>GST No.</span>
            <label>
              <input
                type="text"
                maxLength={15}
                placeholder="Enter GST no."
                className={classes["detail-input"]}
                defaultValue={
                  consigneeData && consigneeData.gst_no
                    ? consigneeData.gst_no.toUpperCase()
                    : ""
                }
                value={consigneeData.gst_no ? consigneeData.gst_no : ""}
                onChange={(e) =>
                  setConsigneeData((prev) => ({
                    ...prev,
                    gst_no: e.target.value.toUpperCase(),
                  }))
                }
                readOnly={readOnly || isRestricted}
              />
            </label>
          </div>
        </div>
        <div className={`row ${classes["table-alignment"]} m-2`}>
          <table className={`${classes["table-width"]} table table-borderless`}>
            <thead>
              <tr
                style={{ fontSize: "14px" }}
                className={classes["row-header"]}
              >
                <th
                  scope="col"
                  className={`${classes["sticky-th"]} ${classes["document-input"]}`}
                  style={{ minWidth: "30px" }}
                >
                  Document No.
                </th>
                <th
                  scope="col"
                  className={`${classes["sticky-th"]} ${classes["material-desc-input"]}`}
                  style={{ minWidth: "200px" }}
                >
                  Material Description
                </th>
                <th
                  scope="col"
                  className={`${classes["sticky-th"]} ${classes["material-code-input"]}`}
                  style={{ minWidth: "100px" }}
                >
                  Material Code
                </th>
                <th
                  scope="col"
                  className={`${classes["sticky-th"]} ${classes["material-code-input"]}`}
                  style={{ minWidth: "100px" }}
                >
                  Quantity
                </th>
                <th
                  scope="col"
                  className={`${classes["sticky-th"]} ${classes["weight-input"]}`}
                  style={{ minWidth: "150px" }}
                >
                  Unit Weight(in KG)
                </th>
                <th
                  scope="col"
                  className={`${classes["sticky-th"]}  ${classes["value-input"]}`}
                  style={{ minWidth: "200px" }}
                >
                  Sales Value (
                  {companySettingData?.default_currency?.default_currency?.cc ??
                    "INR"}
                  )
                </th>
                {userDefinedFields &&
                  userDefinedFields.map((field, i) => (
                    <th
                      key={field.uuid || i}
                      scope="col"
                      className={classes["sticky-th"]}
                      style={{ minWidth: "150px", textAlign: "center" }}
                    >
                      {field.field} <br />
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: "normal",
                          color: "#555",
                        }}
                      >
                        (User Defined)
                      </span>
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody className={classes["scrollable-body"]}>
              {consignmentData?.length > 0
                ? consignmentData.map((consignment, index) => (
                  <React.Fragment key={index}>
                    {consignment.orders_list.map((order, idx) =>
                      order.isNew ? (
                        <tr key={`${index}-${idx}`}>
                          <td>
                            <input
                              type="text"
                              value={consignment.invoice_no ?? ""}
                              onChange={(e) =>
                                updateConsignmentField(
                                  index,
                                  "invoice_no",
                                  e.target.value
                                )
                              }
                              className={classes["document-input"]}
                              readOnly={readOnly || isRestricted}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={order.material_name ?? ""}
                              onChange={(e) =>
                                updateOrderField(
                                  index,
                                  idx,
                                  "material_name",
                                  e.target.value
                                )
                              }
                              className={classes["material-desc-input"]}
                              readOnly={readOnly || isRestricted}
                            />
                          </td>
                          <td>
                            <div
                              className="d-flex align-items-center gap-1 border rounded bg-white"
                              style={{ minWidth: 120 }}
                            >
                              <input
                                type="text"
                                className="form-control form-control-sm border-0 shadow-none p-0 m-0"
                                placeholder="Code"
                                value={order?.material_code ?? ""}
                                onChange={(e) =>
                                  updateOrderField(
                                    index,
                                    idx,
                                    "material_code",
                                    e.target.value
                                  )
                                }
                                style={{ width: "100%" }}
                                readOnly={readOnly || isRestricted}
                              />

                              {!readOnly && !isRestricted && (
                                <button
                                  type="button"
                                  className="btn btn-light btn-sm d-flex align-items-center justify-content-center border"
                                  onClick={() =>
                                    fetchMaterialDetails(
                                      order.material_code,
                                      index,
                                      idx,
                                    )
                                  }
                                  title="Auto-fill from Material Master"
                                  style={{
                                    zIndex: "1",
                                    width: 30,
                                    height: 30,
                                    padding: 0,
                                    flex: "0 0 auto",
                                  }}
                                  disabled={!order.material_code}
                                >
                                  <i className="fa fa-magic text-success" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <input
                              type="number"
                              value={
                                order.quantity === 0 ? "" : order.quantity
                              }
                              onChange={(e) =>
                                updateOrderField(
                                  index,
                                  idx,
                                  "quantity",
                                  e.target.value,
                                )
                              }
                              className={classes["material-code-input"]}
                              readOnly={readOnly || isRestricted}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={
                                order.material_weight === 0
                                  ? ""
                                  : order?.material_weight
                              }
                              onChange={(e) =>
                                updateOrderField(
                                  index,
                                  idx,
                                  "material_weight",
                                  e.target.value,
                                )
                              }
                              className={classes["weight-input"]}
                              readOnly={readOnly || isRestricted}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={
                                order?.invoice_value === 0
                                  ? ""
                                  : order?.invoice_value
                              }
                              onChange={(e) =>
                                updateOrderField(
                                  index,
                                  idx,
                                  "invoice_value",
                                  Number(e.target.value),
                                )
                              }
                              className={classes["value-input"]}
                              readOnly={readOnly || isRestricted}
                            />
                          </td>
                          {userDefinedFields &&
                            userDefinedFields.map((field, i) => (
                              <td key={`input-${index}-${idx}-${i}`}>
                                <input
                                  type={
                                    field.data_type === "number"
                                      ? "number"
                                      : "text"
                                  }
                                  value={order[field.field] ?? ""}
                                  onChange={(e) =>
                                    updateOrderField(
                                      index,
                                      idx,
                                      field.field,
                                      e.target.value,
                                    )
                                  }
                                  className="form-control form-control-sm"
                                  style={{ minWidth: "100px" }}
                                  readOnly={readOnly || isRestricted}
                                />
                              </td>
                            ))}
                        </tr>
                      ) : (
                        <tr key={`${index}-${idx}`}>
                          <td className={classes["document-input"]}>
                            {consignment.invoice_no}
                          </td>
                          <td className={classes["material-desc-input"]}>
                            {order.material_name}
                          </td>
                          <td className={classes["material-code-input"]}>
                            {order.material_code}
                          </td>
                          <td className={classes["material-code-input"]}>
                            {order.quantity}
                          </td>
                          <td className={classes["weight-input"]}>
                            {order.material_weight ?? "--"}
                          </td>
                          <td className={classes["document-input"]}>
                            {order.invoice_value ?? "--"}
                          </td>
                          {userDefinedFields &&
                            userDefinedFields.map((field, i) => (
                              <td
                                key={`view-${index}-${idx}-${i}`}
                                style={{ textAlign: "center" }}
                              >
                                {order[field.field] ?? "--"}
                              </td>
                            ))}
                        </tr>
                      ),
                    )}
                  </React.Fragment>
                ))
                : null}
            </tbody>
          </table>
        </div>
        <div className="d-flex align-items-center justify-content-between">
          {!readOnly && !isRestricted && (
            <div className="bg-light py-1 px-2 mb-2 d-flex align-items-center justify-content-start gap-2 small text-muted text-left">
              <span>
                * Enter <strong className="text-dark">Material Code</strong> and
                click <i className="fa fa-magic" aria-hidden="true" /> to
                auto-fill from
                <strong className="text-dark"> Material Master</strong>.
              </span>
            </div>
          )}

          {!readOnly && (
            <div className={`${classes["sales-count"]}`}>
              <div style={{ fontWeight: "bolder", padding: "2%" }}>
                # Sales Invoices:
              </div>
              {!readOnly && !isRestricted && (
                <button
                  className={`btn ${classes["counter-btn"]}`}
                  onClick={(e) => handleRemoveRow()}
                  disabled={
                    details?.lr_details &&
                      details?.lr_details?.length > 0 &&
                      details?.invoice_status
                      ? true
                      : false
                  }
                  style={{
                    backgroundColor: "#393185",
                    color: "white",
                    cursor: "pointer",
                    fontWeight: "bolder",
                    margin: "1%!important",
                  }}
                >
                  -
                </button>
              )}
              <input
                type="number"
                value={invoiceCount}
                className={`${classes["input-count"]}`}
                disabled={readOnly || isRestricted}
              />
              {!readOnly && !isRestricted && (
                <button
                  className={`btn ${classes["counter-btn"]}`}
                  onClick={(e) => handleAddRow()}
                  disabled={
                    details?.lr_details &&
                      details?.lr_details?.length > 0 &&
                      details?.invoice_status
                      ? true
                      : false
                  }
                  style={{
                    backgroundColor: "#393185",
                    color: "white",
                    cursor: "pointer",
                    fontWeight: "bolder",
                    margin: "1%!important",
                  }}
                >
                  +
                </button>
              )}
            </div>
          )}
        </div>
        {(!readOnly || isRestricted) && !hideSubmitButton && (
          <div
            onClick={() =>
              isRestricted &&
              toast.info("You don't have permission to perform this action.")
            }
            style={{
              display: "inline-block",
              cursor: isRestricted ? "not-allowed" : "pointer",
            }}
          >
            <button
              className={`btn ${classes["submit-btn"]}`}
              onClick={() =>
                !isRestricted && handleSubmit(consigneeData, consignorData)
              }
              disabled={
                readOnly ||
                isRestricted ||
                (details?.lr_details &&
                  details?.lr_details?.length > 0 &&
                  details?.invoice_status &&
                  details?.lr_generated !== false
                  ? true
                  : false)
              }
              style={{
                ...(isRestricted
                  ? { opacity: 0.6, pointerEvents: "none" }
                  : {}),
              }}
            >
              Submit
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsignmentDetails;